import React, { useState } from "react";
import { useT } from "@/lib/LanguageContext";
import CrmDialog from "../ui/CrmDialog";
import CrmInput from "../ui/CrmInput";
import CrmTextarea from "../ui/CrmTextarea";
import CrmButton from "../ui/CrmButton";
import MergeFieldPicker, { insertAtCaret } from "../ui/MergeFieldPicker";
import { LABEL } from "../ui/classes";
import useEmailPreview from "./preview/useEmailPreview";
import EmailPreviewFrame from "./preview/EmailPreviewFrame";
import { CRM } from "../../../constants/testIds/crm";

/**
 * "Edit message" (spec: the birthday toggle's only configuration): subject and body,
 * pre-filled with what is saved. `onSave(subject, body_html)` keeps the CURRENT
 * `enabled` value — BirthdayCard owns the toggle, this dialog only edits the copy.
 *
 * Preview and "Send test to me" act on the copy being edited, unsaved: the preview shows
 * it as a client receives it and disappears as soon as the copy changes again.
 */
export default function BirthdayMessageDialog({ open, onOpenChange, subject, bodyHtml, onSave, busy }) {
  const t = useT("crm");
  const [draftSubject, setDraftSubject] = useState(subject);
  const [draftBody, setDraftBody] = useState(bodyHtml);
  const area = React.useRef(null);
  const preview = useEmailPreview({ kind: "birthday", subject: draftSubject, body_html: draftBody });
  const incomplete = !draftSubject.trim() || !draftBody.trim();

  // The dialog opens fresh from the saved copy every time (never carries a stale edit
  // from a previous open the user cancelled out of).
  React.useEffect(() => {
    if (open) { setDraftSubject(subject); setDraftBody(bodyHtml); }
  }, [open, subject, bodyHtml]);

  const insert = (token) => {
    const { value, caret } = insertAtCaret(area.current, draftBody, token);
    setDraftBody(value);
    requestAnimationFrame(() => { area.current?.focus(); area.current?.setSelectionRange(caret, caret); });
  };

  return (
    <CrmDialog open={open} onOpenChange={onOpenChange} size="lg" testId={CRM.emailBirthdayDialog}
      title={t("email.birthdayDialogTitle")}
      footer={(
        <>
          <CrmButton variant="link" onClick={() => onOpenChange(false)}>{t("common.cancel")}</CrmButton>
          <CrmButton onClick={() => onSave(draftSubject, draftBody)} disabled={busy || incomplete} data-testid={CRM.emailBirthdaySave}>
            {t("common.save")}
          </CrmButton>
        </>
      )}>
      <div className="space-y-3">
        <div>
          <label htmlFor="crm-birthday-subject" className={LABEL}>{t("seq.subjectLabel")}</label>
          <CrmInput id="crm-birthday-subject" value={draftSubject} onChange={(e) => setDraftSubject(e.target.value)} data-testid={CRM.emailBirthdaySubject} />
        </div>
        <div>
          <label htmlFor="crm-birthday-body" className={LABEL}>{t("seq.bodyLabel")}</label>
          <CrmTextarea id="crm-birthday-body" ref={area} rows={6} value={draftBody} onChange={(e) => setDraftBody(e.target.value)} data-testid={CRM.emailBirthdayBody} />
        </div>
        <MergeFieldPicker onInsert={insert} fields={["{client_name}", "{practice_name}"]} />
        <div className="flex flex-wrap items-center gap-3">
          <CrmButton variant="outline" size="sm" disabled={incomplete || preview.loading} onClick={preview.load} data-testid={CRM.emailBirthdayPreview}>
            {t("emailPreview.preview")}
          </CrmButton>
          <CrmButton variant="outline" size="sm" disabled={incomplete || preview.sending} onClick={preview.sendTest} data-testid={CRM.emailBirthdaySendTest}>
            {t("emailPreview.sendTestToMe")}
          </CrmButton>
        </div>
        {preview.email && <EmailPreviewFrame email={preview.email} />}
      </div>
    </CrmDialog>
  );
}
